import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { DASHBOARDS, APP_FLAGS, canOpen } from '../dashboards';
import { Logo, UserChip, MenuLink } from './PageHeader';

const KNOWN = APP_FLAGS.map(f => f.flag);
const flagLabel = f => APP_FLAGS.find(x => x.flag === f)?.label || f;

const fDate = d => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const fDateTime = d => d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const daysSince = d => d ? Math.floor((Date.now() - new Date(d)) / 86400000) : null;

export default function UsersPage({ session, onSignOut }) {
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const [u, a] = await Promise.all([
      supabase.rpc('admin_list_users'),
      supabase.rpc('admin_user_audit', { p_limit: 100 }),
    ]);
    if (u.error) { setError(u.error.message); return; }
    setError(null);
    setUsers(u.data || []);
    setAudit(a.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async ({ email, full_name, apps, is_active }) => {
    const { error: e } = await supabase.rpc('admin_upsert_user', {
      p_email: email, p_full_name: full_name, p_apps: apps, p_is_active: is_active,
    });
    if (e) return e.message;
    await load();
    return null;
  };

  return (
    <div style={{ maxWidth: 1480, margin: '0 auto', padding: '24px 24px 60px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <MenuLink />
          <Logo />
        </div>
        <UserChip session={session} onSignOut={onSignOut} />
      </header>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>User Management</h2>

      {error && <div style={{ padding: 60, textAlign: 'center', color: 'var(--red)' }}>Could not load: {error}</div>}
      {!error && users === null && <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>}
      {!error && users && (
        <>
          <Summary users={users} />
          <AddUser existing={users} onSave={save} />
          <UsersTable users={users} me={session.user.email} onSave={save} />
          <AuditLog rows={audit} />
        </>
      )}
    </div>
  );
}

function Summary({ users }) {
  const active = users.filter(u => u.is_active);
  const cards = [
    { label: 'Active users', value: active.length },
    { label: 'System admins', value: active.filter(u => u.apps.includes('systemadmin')).length },
    { label: 'Finance access', value: active.filter(u => u.apps.includes('finance') || u.apps.includes('systemadmin')).length },
    { label: 'Never signed in', value: active.filter(u => !u.last_sign_in_at).length, warn: true },
    { label: 'No sign-in 90+ days', value: active.filter(u => daysSince(u.last_sign_in_at) >= 90).length, warn: true },
    { label: 'Inactive', value: users.length - active.length },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 12, marginBottom: 20 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text2)', marginBottom: 6 }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Mono,monospace', color: c.warn && c.value > 0 ? 'var(--amber)' : 'var(--text)' }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function AddUser({ existing, onSave }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const norm = email.trim().toLowerCase();
  const dupe = existing.some(u => u.email === norm);

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    const err = await onSave({ email: norm, full_name: name, apps, is_active: true });
    setBusy(false);
    if (err) setMsg({ err: true, text: err });
    else { setMsg({ text: `Added ${norm}.` }); setEmail(''); setName(''); setApps([]); }
  };

  if (!open) {
    return (
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => { setOpen(true); setMsg(null); }} style={primaryBtn}>+ Add user</button>
        {msg && <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--green)' }}>{msg.text}</span>}
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <input placeholder="Email" type="email" required value={email} onChange={e => setEmail(e.target.value)} style={{ ...inputStyle, width: 280 }} />
        <input placeholder="Full name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
      </div>
      <FlagPicker apps={apps} onChange={setApps} />
      {dupe && <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 10 }}>This email is already on the allowlist; saving will overwrite its flags and reactivate it.</div>}
      {msg?.err && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 10 }}>{msg.text}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="submit" disabled={busy || !norm} style={primaryBtn}>{busy ? 'Saving…' : dupe ? 'Update user' : 'Add user'}</button>
        <button type="button" onClick={() => setOpen(false)} style={secondaryBtn}>Cancel</button>
      </div>
    </form>
  );
}

// Toggle chips for every known flag, plus any legacy/unknown flag the row already has.
function FlagPicker({ apps, onChange, disabledFlags = [] }) {
  const flags = [...KNOWN, ...apps.filter(f => !KNOWN.includes(f))];
  const toggle = f => onChange(apps.includes(f) ? apps.filter(x => x !== f) : [...apps, f]);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {flags.map(f => {
        const on = apps.includes(f);
        const legacy = !KNOWN.includes(f);
        const locked = disabledFlags.includes(f);
        const meta = APP_FLAGS.find(x => x.flag === f);
        return (
          <button
            key={f}
            type="button"
            disabled={locked}
            onClick={() => toggle(f)}
            title={locked ? 'You cannot remove your own systemadmin access' : legacy ? 'Legacy flag; no page uses it any more' : meta?.description}
            style={{
              ...chip,
              cursor: locked ? 'not-allowed' : 'pointer',
              opacity: locked ? 0.6 : 1,
              background: on ? (legacy ? 'var(--amber-bg)' : 'var(--green-bg)') : 'transparent',
              borderColor: on ? (legacy ? 'var(--amber)' : 'var(--green)') : 'var(--border)',
              color: on ? (legacy ? 'var(--amber)' : 'var(--green)') : 'var(--text3)',
              textDecoration: legacy && !on ? 'line-through' : 'none',
            }}
          >
            {on ? '✓ ' : ''}{flagLabel(f)}{legacy ? ' (legacy)' : ''}
          </button>
        );
      })}
    </div>
  );
}

function UsersTable({ users, me, onSave }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [flag, setFlag] = useState('all');

  const flagsInUse = useMemo(() => [...new Set([...KNOWN, ...users.flatMap(u => u.apps)])], [users]);

  const filtered = useMemo(() => users.filter(u => {
    const q = search.toLowerCase();
    if (q && !(u.email.includes(q) || (u.full_name || '').toLowerCase().includes(q))) return false;
    if (status === 'active' && !u.is_active) return false;
    if (status === 'inactive' && u.is_active) return false;
    if (status === 'never' && (u.last_sign_in_at || !u.is_active)) return false;
    if (status === 'stale' && (!u.is_active || daysSince(u.last_sign_in_at) < 90 || !u.last_sign_in_at)) return false;
    if (flag !== 'all' && !u.apps.includes(flag)) return false;
    return true;
  }), [users, search, status, flag]);

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} style={inputStyle} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="never">Active, never signed in</option>
          <option value="stale">Active, no sign-in 90+ days</option>
          <option value="all">All users</option>
        </select>
        <select value={flag} onChange={e => setFlag(e.target.value)} style={selectStyle}>
          <option value="all">Any flag</option>
          {flagsInUse.map(f => <option key={f} value={f}>{flagLabel(f)}</option>)}
        </select>
      </div>

      <div style={tableWrap}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: 'var(--card2)' }}>
              {['User', 'Status', 'Flags', 'Can open', 'Last sign-in', 'Added', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => <UserRow key={u.email} user={u} isMe={u.email === me.toLowerCase()} onSave={onSave} />)}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text3)', padding: 32 }}>No users match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UserRow({ user, isMe, onSave }) {
  const [apps, setApps] = useState(user.apps);
  const [active, setActive] = useState(user.is_active);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Reset staged edits whenever the server row changes (after a save/reload)
  useEffect(() => { setApps(user.apps); setActive(user.is_active); setErr(null); }, [user]);

  const dirty = active !== user.is_active || apps.length !== user.apps.length || apps.some(a => !user.apps.includes(a));
  const opens = active ? DASHBOARDS.filter(d => canOpen(apps, d)) : [];
  const age = daysSince(user.last_sign_in_at);

  const submit = async () => {
    setBusy(true);
    const e = await onSave({ email: user.email, full_name: user.full_name, apps, is_active: active });
    setBusy(false);
    setErr(e);
  };

  return (
    <tr style={{ borderTop: '1px solid var(--border)', background: dirty ? 'var(--warn-bg)' : undefined, opacity: active ? 1 : 0.65 }}>
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{user.full_name || '—'}{isMe && <span style={meTag}>you</span>}</div>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>{user.email}</div>
      </td>
      <td style={tdStyle}>
        <button
          type="button"
          disabled={isMe}
          onClick={() => setActive(!active)}
          title={isMe ? 'You cannot deactivate yourself' : active ? 'Click to deactivate' : 'Click to reactivate'}
          style={{ ...chip, cursor: isMe ? 'not-allowed' : 'pointer', background: active ? 'var(--green-bg)' : 'var(--red-bg)', borderColor: 'transparent', color: active ? 'var(--green)' : 'var(--red)' }}
        >
          {active ? 'Active' : 'Inactive'}
        </button>
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'normal', minWidth: 320 }}>
        <FlagPicker apps={apps} onChange={setApps} disabledFlags={isMe ? ['systemadmin'] : []} />
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'normal', fontSize: 12, color: 'var(--text2)', maxWidth: 220 }}>
        {opens.length ? opens.map(d => d.title).join(', ') : <span style={{ color: 'var(--text3)' }}>Nothing</span>}
      </td>
      <td style={{ ...tdStyle, fontSize: 12 }}>
        {!user.has_account
          ? <span style={{ color: 'var(--amber)' }}>Never signed in</span>
          : <span title={fDateTime(user.last_sign_in_at)} style={{ color: age >= 90 ? 'var(--amber)' : 'var(--text)' }}>
              {fDate(user.last_sign_in_at)}{age != null && <span style={{ color: 'var(--text3)' }}> · {age}d ago</span>}
            </span>}
      </td>
      <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text2)' }}>
        {fDate(user.added_at)}{user.added_by && <div style={{ color: 'var(--text3)' }}>by {user.added_by}</div>}
      </td>
      <td style={{ ...tdStyle, minWidth: 150 }}>
        {dirty && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={submit} disabled={busy} style={{ ...primaryBtn, padding: '5px 12px', fontSize: 12 }}>{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => { setApps(user.apps); setActive(user.is_active); setErr(null); }} style={{ ...secondaryBtn, padding: '5px 12px', fontSize: 12 }}>Undo</button>
          </div>
        )}
        {err && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4, whiteSpace: 'normal' }}>{err}</div>}
      </td>
    </tr>
  );
}

function AuditLog({ rows }) {
  const [open, setOpen] = useState(false);
  const describe = r => {
    if (r.action === 'insert') return `Added with ${(r.new_apps || []).map(flagLabel).join(', ') || 'no flags'}`;
    if (r.action === 'delete') return 'Removed from allowlist';
    const parts = [];
    const added = (r.new_apps || []).filter(a => !(r.old_apps || []).includes(a));
    const removed = (r.old_apps || []).filter(a => !(r.new_apps || []).includes(a));
    if (added.length) parts.push(`+ ${added.map(flagLabel).join(', ')}`);
    if (removed.length) parts.push(`− ${removed.map(flagLabel).join(', ')}`);
    if (r.old_active !== r.new_active) parts.push(r.new_active ? 'Reactivated' : 'Deactivated');
    if ((r.old_name || '') !== (r.new_name || '')) parts.push('Name changed');
    return parts.join(' · ') || 'No access change';
  };

  return (
    <div style={{ marginTop: 28 }}>
      <button onClick={() => setOpen(!open)} style={{ ...secondaryBtn, marginBottom: 12 }}>
        {open ? '▾' : '▸'} Change history ({rows.length})
      </button>
      {open && (
        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>{['When', 'User', 'Change', 'By'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text2)' }}>{fDateTime(r.changed_at)}</td>
                  <td style={{ ...tdStyle, fontSize: 12 }}>{r.email}</td>
                  <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'normal' }}>{describe(r)}</td>
                  <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text2)' }}>{r.changed_by || 'SQL editor'}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text3)', padding: 24 }}>No changes recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const inputStyle = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', color: 'var(--text)', fontSize: 13, width: 240, outline: 'none' };
const selectStyle = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', color: 'var(--text)', fontSize: 13, cursor: 'pointer', outline: 'none' };
const primaryBtn = { background: 'var(--green)', color: 'var(--on-green)', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const secondaryBtn = { background: 'var(--card2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const chip = { border: '1px solid var(--border)', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600 };
const meTag = { fontSize: 9, background: 'var(--blue-bg)', color: 'var(--blue)', borderRadius: 3, padding: '1px 5px', fontWeight: 600, marginLeft: 6 };
const tableWrap = { overflowX: 'auto', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--card)', boxShadow: '0 1px 3px rgba(0,0,0,.04)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '11px 14px', textAlign: 'left', fontWeight: 600, fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.7px', color: 'var(--text2)', whiteSpace: 'nowrap', background: 'var(--card2)' };
const tdStyle = { padding: '9px 14px', whiteSpace: 'nowrap', verticalAlign: 'middle' };
